import {
  Hourglass,
  MessageCircle,
  PenLine,
  ThumbsUp,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { View, XStack, YGroup, YStack } from "tamagui";
import { useApi } from "../../core/apis";
import { ModuleBlock } from "../../ui/Block";
import { Body, BodyTextarea } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { ModuleEditor } from "../../ui/editor/useModuleEditor";
import { Group } from "../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../ui/InfoRow";
import { Label } from "../../ui/Label";
import { log } from "../../ui/log";
import { Separator } from "../../ui/Separator";
import { AvatarSize, AvatarSizeLookup } from "../../ui/UserAvatar";
import { UserUI } from "../../users/UserUI";
import { DoneBox } from "../elements/DoneBox";
import { RsvpStatus } from "./data/RsvpUserModel";
import type RsvpModuleModel from "./RsvpModuleModel";
import type { UserRsvpWithStatus } from "./RsvpModuleModel";

const RsvpColors: Record<RsvpStatus, string> = {
  [RsvpStatus.Coming]: "#4CAF50",
  [RsvpStatus.Maybe]: "#FFC107",
  [RsvpStatus.No]: "#F44336",
  [RsvpStatus.NoAnswerYet]: "#9E9E9E",
};

export const RsvpModuleStaticUI = observer(
  (_props: { module: RsvpModuleModel }) => {
    return <Label>Static Content - TODO</Label>;
  },
);

const _UserList = observer(
  ({
    users,
    spotsLimit,
  }: {
    users: UserRsvpWithStatus[];
    spotsLimit: number;
  }) => {
    if (users.length === 0) {
      return null;
    }

    return (
      <Group>
        {users.map((u, i) => {
          const isDimmed = spotsLimit > 0 && i >= spotsLimit;

          return (
            <View
              key={u.userId}
              flex={1}
              width={"100%"}
              opacity={isDimmed ? 0.2 : 1}
            >
              <UserUI uid={u.userId} />
              <View
                position={"absolute"}
                style={{ backgroundColor: RsvpColors[u.rsvp] }}
                right={-12}
                top={-12}
                width={10}
                bottom={-12}
              />
            </View>
          );
        })}
      </Group>
    );
  },
);

const RsvpUserItem = observer(
  ({ user, subText }: { user: UserRsvpWithStatus; subText: string }) => {
    return (
      <YStack gap="$small" key={user.userId}>
        <UserUI uid={user.userId} subText={subText} />
        {!!user.comment && (
          <XStack gap="$small" alignItems="center">
            <MessageCircle
              size={AvatarSizeLookup[AvatarSize.Smaller]}
              color={"$muted"}
            />
            <Body fontStyle={"italic"} color={"$muted"}>
              '{user.comment}'
            </Body>
          </XStack>
        )}
      </YStack>
    );
  },
);

const RsvpOption = observer(
  ({
    label,
    emoji,
    color,
    selected,
    onPress,
  }: {
    label: string;
    emoji: string;
    color: string;
    onPress: () => void;
    selected?: boolean;
  }) => {
    return (
      <YStack
        flex={1}
        onPress={onPress}
        cursor="pointer"
        gap={"$medium"}
        style={{ borderColor: color }}
        padding={"$medium"}
        paddingVertical={"$large"}
        alignItems={"center"}
        background={selected ? color : "$backgroundBlock"}
        borderWidth={2}
      >
        <Label fontSize={24}>{emoji}</Label>
        <Label>{label}</Label>
      </YStack>
    );
  },
);

export const RsvpModuleUI = observer(
  ({ module }: { module: RsvpModuleModel }) => {
    const { modulesDataApi } = useApi();

    const moduleUid = module.uid;

    React.useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(moduleUid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${moduleUid}`,
        );
        unsub();
      };
    }, [modulesDataApi, moduleUid]);

    return (
      <YStack gap="$medium">
        {module.data.settings.showDescription && (
          <Box marginBottom="$medium">
            <ModuleEditor module={module} />
          </Box>
        )}

        <Box>
          <Label>Can you attend?</Label>
          <Separator noMargins />
          <XStack gap="$medium">
            <RsvpOption
              label={"Yes"}
              emoji={"👍"}
              color={RsvpColors.coming}
              selected={module.currentRsvp?.data.rsvp === RsvpStatus.Coming}
              onPress={() => {
                module.addRsvp(RsvpStatus.Coming);
              }}
            />

            <RsvpOption
              label={"Maybe"}
              emoji={"🤔"}
              color={RsvpColors.maybe}
              selected={module.currentRsvp?.data.rsvp === RsvpStatus.Maybe}
              onPress={() => {
                module.addRsvp(RsvpStatus.Maybe);
              }}
            />

            <RsvpOption
              label={"No"}
              emoji={"✋"}
              color={RsvpColors.no}
              selected={module.currentRsvp?.data.rsvp === RsvpStatus.No}
              onPress={() => {
                module.addRsvp(RsvpStatus.No);
              }}
            />
          </XStack>

          {!!module.currentRsvp && (
            <BodyTextarea
              type="border"
              icon={PenLine}
              clearOnSubmit
              allowEmptyTextSubmit
              submitOnEnter
              submitOnBlur
              defaultValue={module.currentRsvp?.data.comment ?? ""}
              onSubmitEditing={(e) =>
                module.currentRsvp?.setComment(e.nativeEvent.text)
              }
              placeholder="Add comment (Optional) ..."
            />
          )}
        </Box>

        <Box>
          <YGroup gap="$small">
            <Label>Results</Label>
            <InfoRow
              items={[
                { icon: "👍", text: `${module.comingUsers.length} attending` },
                module.maybeUsers.length
                  ? {
                      icon: "🤔",
                      text: `${module.maybeUsers.length} maybe`,
                    }
                  : undefined,
                module.notComingUsers.length
                  ? {
                      icon: "✋",
                      text: `${module.notComingUsers.length} not attending`,
                    }
                  : undefined,
                module.notAnsweredUsers.length
                  ? {
                      icon: "❓",
                      text: `${module.notAnsweredUsers.length} not answered`,
                    }
                  : undefined,
              ]}
            />
          </YGroup>
          <Group>
            {module.comingUsers.map((user) => {
              return (
                <RsvpUserItem
                  key={user.userId}
                  user={user}
                  subText={"👍 Coming"}
                />
              );
            })}

            {module.maybeUsers.map((user) => {
              return (
                <RsvpUserItem
                  key={user.userId}
                  user={user}
                  subText={"🤔 Maybe coming"}
                />
              );
            })}

            {module.notAnsweredUsers.map((user) => {
              return (
                <RsvpUserItem
                  key={user.userId}
                  user={user}
                  subText={"❓ Waiting for answer"}
                />
              );
            })}

            {module.notComingUsers.map((user) => {
              return (
                <RsvpUserItem
                  key={user.userId}
                  user={user}
                  subText={"✋ Not coming"}
                />
              );
            })}
          </Group>
        </Box>

        <DoneBox
          ctaLabel={"Finish to close"}
          userIsDone={module.hasAddedRsvp}
          waitingForUsers={module.waitingForUsers}
          module={module}
          userReadyUpDescription="Add your own RSVP and wait for the rest to do the same"
          beforeDoneDescription="When everyone is ready, you can mark the this step as finished close it"
          everyoneReadyDescription="Everyone has added their RSVP, mark the card as finished to close it"
          doneDescription="Great! You are finished for this time."
        />
      </YStack>
    );
  },
);

export const RsvpModuleBlockUI = observer(
  ({ module }: { module: RsvpModuleModel }) => {
    const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
      const rows = [
        {
          text: `Yes (${module.data.snapshot?.numYes ?? 0})`,
          icon: ThumbsUp,
        },
      ];

      if (module.data.snapshot?.numMaybe > 0) {
        rows.push({
          text: `Maybe (${module.data.snapshot?.numMaybe ?? 0})`,
          icon: Hourglass,
        });
      }

      return rows;
    }, [module.data.snapshot]);

    let doneText = `Yes (${module.data.snapshot?.numYes})`;

    if (module.data.snapshot?.numMaybe > 0) {
      doneText += `, Maybe (${module.data.snapshot?.numMaybe})`;
    }

    return (
      <ModuleBlock
        module={module}
        infoRowItems={infoRowItems}
        doneText={doneText}
      />
    );
  },
);
